import React from 'react';
import { WeatherCondition, WeatherDay } from '@models/weather.model';
import { WEATHER_CONDITION_ICON_PATHS } from './weatherGlyphPaths';

export type WeatherGlyphVariant = 'forecast' | 'observed';

/** `zh-weather-condition--<kebab-case condition>` — CSS-driven color per `CalendarStyles.css`'s
 * `.zh-weather` rules (light + `.dark` override), so the glyph never hard-codes a hex color
 * itself. `fill`/`stroke` use `currentColor`, matching the CSS `color:` value for this class. */
export const weatherConditionClassName = (condition: WeatherCondition): string =>
  `zh-weather-condition--${condition.toLowerCase().replace(/_/g, '-')}`;

interface WeatherGlyphProps {
  condition: WeatherCondition;
  variant: WeatherGlyphVariant;
  size?: number;
  className?: string;
  title?: string;
}

/**
 * Sprint 25, story-133. Forecast = outline stroke (no fill); Observed = filled — the UX doc's
 * "legend = outline = forecast, solid = observed." `HEAVY_RAIN` renders two overlapping copies of
 * the same `Grain` glyph `DRIZZLE` uses, at a visibly denser cluster, to read as "the same
 * condition family, more of it" without a second icon asset.
 */
export const WeatherGlyph: React.FC<WeatherGlyphProps> = ({ condition, variant, size = 14, className, title }) => {
  const paths = WEATHER_CONDITION_ICON_PATHS[condition] ?? WEATHER_CONDITION_ICON_PATHS.CLEAR;
  const isObserved = variant === 'observed';
  const isHeavyRain = condition === 'HEAVY_RAIN';

  return (
    <svg
      className={`zh-weather-glyph ${weatherConditionClassName(condition)} ${className ?? ''}`.trim()}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d, index) => (
        <path
          key={index}
          d={d}
          fill={isObserved ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth={isObserved ? 0 : 1.5}
          transform={isHeavyRain && index === 0 ? 'translate(-2 -2) scale(0.85)' : undefined}
        />
      ))}
    </svg>
  );
};

/** Escapes text for safe embedding inside an HTML attribute/content string — mirrors
 * `TimeLine/index.tsx`'s own `escapeHtmlAttr`, duplicated here (not imported) so this module has
 * no dependency on the calendar file, only the reverse. */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Renders a `WeatherDay` as a static HTML string for FullCalendar's DOM-injection content hooks
 * (`slotLabelContent`/`dayCellContent` return `{ html }`) — FullCalendar does not render React
 * trees, so the glyph markup above is reproduced here as a string using the same paths/classes.
 * `tooltipText` is pre-built by the caller (`buildWeatherTooltipText`, translation-aware) and
 * applied as both `title` (hover, and the UX doc's documented fallback for keyboard focus) and
 * `aria-label` (screen readers). Returns `null` for a day with no condition to show (`UNAVAILABLE`
 * or missing) — callers render nothing for those, per the AC.
 */
export const renderWeatherGlyphHtml = (
  day: Pick<WeatherDay, 'kind' | 'condition' | 'highTemp'>,
  tooltipText: string,
): string | null => {
  if (day.kind === 'UNAVAILABLE' || !day.condition) return null;

  const variant: WeatherGlyphVariant = day.kind === 'OBSERVED' ? 'observed' : 'forecast';
  const isObserved = variant === 'observed';
  const isHeavyRain = day.condition === 'HEAVY_RAIN';
  const paths = WEATHER_CONDITION_ICON_PATHS[day.condition] ?? WEATHER_CONDITION_ICON_PATHS.CLEAR;

  const pathMarkup = paths
    .map((d, index) => {
      const transform = isHeavyRain && index === 0 ? ' transform="translate(-2 -2) scale(0.85)"' : '';
      return `<path d="${d}" fill="${isObserved ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="${
        isObserved ? 0 : 1.5
      }"${transform}></path>`;
    })
    .join('');

  const svg = `<svg class="zh-weather-glyph ${weatherConditionClassName(day.condition)}" width="14" height="14" viewBox="0 0 24 24" focusable="false" aria-hidden="true">${pathMarkup}</svg>`;

  const tempMarkup =
    day.highTemp != null
      ? `<span class="zh-weather-temp">${Math.round(day.highTemp)}°</span>`
      : '';

  return `<span class="zh-weather ${weatherConditionClassName(day.condition)}" tabindex="0" role="img" title="${escapeHtml(
    tooltipText,
  )}" aria-label="${escapeHtml(tooltipText)}">${svg}${tempMarkup}</span>`;
};

export default WeatherGlyph;
